import { Radio, RadioGroup } from '@headlessui/react'
import { Link } from '@remix-run/react'
import { CheckCircle } from 'lucide-react'
import { useState } from 'react'

import { cn } from '#app/utils/misc.tsx'

const monthly = { value: 'monthly', label: 'Monthly', priceSuffix: '/month' }
const frequencies = [
	monthly,
	{ value: 'annually', label: 'Annually', priceSuffix: '/month' },
]
const tiers = [
	{
		name: 'Hobby',
		id: 'tier-hobby',
		href: 'https://cal.com/stanliu/subdark-demo',
		price: 'Free',
		description: 'For hobbyist developers looking to start an API product.',
		features: [
			'Limit 1 application',
			'No teammates',
			'7-day log history',
			'Community support',
			'50% revenue share',
		],
		featured: false,
		cta: 'Book a demo',
	},
	{
		name: 'Startup',
		id: 'tier-startup',
		href: 'https://cal.com/stanliu/subdark-demo',
		price: { monthly: '$500', annually: '$400' },
		description:
			'For professional developers and teams shipping to production.',
		features: [
			'Limit 1 application',
			'Unlimited teammates',
			'30-day log history',
			'24-hour support response time',
			'Advanced analytics',
			'25% revenue share',
		],
		featured: false,
		cta: 'Book a demo',
	},
	{
		name: 'Enterprise',
		id: 'tier-enterprise',
		href: 'https://cal.com/stanliu/subdark-demo',
		price: 'Custom',
		description: 'Dedicated support and infrastructure for your company.',
		features: [
			'Unlimited applications',
			'Unlimited team seats',
			'Unlimited log history',
			'1-hour, dedicated support response time',
			'Advanced analytics',
			'No revenue share',
		],
		featured: true,
		cta: 'Book a demo',
	},
]

export function Pricing() {
	const [frequency, setFrequency] = useState(monthly)

	return (
		<div className="py-12" id="pricing">
			<div className="mx-auto max-w-7xl px-6 lg:px-8">
				<div className="mx-auto max-w-4xl text-center">
					<h2 className="text-sm font-bold uppercase tracking-wide">Pricing</h2>
					<p className="mt-4 text-4xl font-bold sm:text-5xl">
						Simple Pricing. Start for Free.
					</p>
				</div>
				<p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-neutral-500 dark:text-muted-foreground">
					Get started for free and scale as you go.
				</p>
				<div className="mt-8 flex justify-center">
					<fieldset aria-label="Payment frequency">
						<RadioGroup
							value={frequency}
							onChange={setFrequency}
							className="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs font-semibold leading-5 ring-1 ring-inset ring-neutral-200"
						>
							{frequencies.map((option) => (
								<Radio
									key={option.value}
									value={option}
									className="cursor-pointer rounded-full px-2.5 py-1 text-neutral-500 data-[checked]:bg-neutral-900 data-[checked]:text-white dark:data-[checked]:bg-neutral-100 dark:data-[checked]:text-black"
								>
									{option.label}
								</Radio>
							))}
						</RadioGroup>
					</fieldset>
				</div>
				<div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
					{tiers.map((tier) => (
						<div
							key={tier.id}
							className={cn(
								tier.featured
									? 'bg-neutral-900 ring-neutral-900 dark:bg-neutral-100 dark:ring-neutral-100'
									: 'ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800',
								'rounded-3xl p-8 ring-1 xl:p-10',
							)}
						>
							<h3
								id={tier.id}
								className={cn(
									tier.featured ? 'text-white dark:text-black' : '',
									'text-xl font-semibold tracking-wide',
								)}
							>
								{tier.name}
							</h3>
							<p
								className={cn(
									tier.featured
										? 'text-neutral-300 dark:text-neutral-500'
										: 'dark:text-neutral-400',
									'mt-4 text-base leading-6',
								)}
							>
								{tier.description}
							</p>
							<p className="mt-6 flex items-baseline gap-x-1">
								<span
									className={cn(
										tier.featured
											? 'text-white dark:text-black'
											: 'text-neutral-900 dark:text-neutral-100',
										'text-4xl font-bold tracking-tight',
									)}
								>
									{typeof tier.price === 'string'
										? tier.price
										: (tier.price as any)[frequency.value]}
								</span>
								{typeof tier.price !== 'string' ? (
									<span
										className={cn(
											tier.featured
												? 'text-neutral-300'
												: 'text-neutral-600 dark:text-neutral-400',
											'text-base font-medium leading-6',
										)}
									>
										{frequency.priceSuffix}
									</span>
								) : null}
							</p>
							<Link
								to={tier.href}
								target="_blank"
								aria-describedby={tier.id}
								className={cn(
									tier.featured
										? 'bg-neutral-100 text-black hover:bg-neutral-100/90 focus-visible:outline-white dark:bg-neutral-900 dark:text-white dark:hover:bg-neutral-900/90'
										: 'bg-neutral-900 text-white shadow-sm hover:bg-neutral-900/90 focus-visible:outline-neutral-900 dark:bg-neutral-100 dark:text-black dark:hover:bg-neutral-100/90',
									'mt-6 block rounded-md px-3 py-2 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
								)}
							>
								{tier.cta}
							</Link>
							<ul
								role="list"
								className={cn(
									tier.featured ? 'text-neutral-300' : 'text-neutral-600',
									'mt-8 space-y-3 text-sm leading-6 xl:mt-10',
								)}
							>
								{tier.features.map((feature) => (
									<li key={feature} className="flex items-center gap-x-3">
										<CheckCircle
											aria-hidden="true"
											className={cn(
												tier.featured
													? 'text-white dark:text-neutral-600'
													: 'text-neutral-600 dark:text-neutral-400',
												'h-4 w-4 flex-none',
											)}
										/>
										<span
											className={
												tier.featured
													? 'text-white dark:text-neutral-600'
													: 'text-neutral-600 dark:text-neutral-400'
											}
										>
											{feature}
										</span>
									</li>
								))}
							</ul>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}
